<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h3 class="text-white font-bold">Edit Invoice</h3>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <div class="rounded bg-white shadow-lg p-12">
            <InvoiceForm
              :default-data="defaultData"
              :default-lines="defaultLines"
              :recipient-options="recipientOptions"
              :issuer-options="issuerOptions"
              :disabled="isLoading"
              :server-errors="serverErrors"
              @invoice-form:submit="doUpdate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { omit } from 'lodash-es';
import { mapActions } from 'vuex';
import { invoicesClient } from '@/api/clients';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import InvoiceFormVue from '@/components/Invoices/InvoiceForm.vue';
import store from '@/store';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    InvoiceForm: InvoiceFormVue,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
    invoiceUuid: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      status: STATUS_IDLE,
      serverErrors: {},
      errorMessage: '',
      defaultData: {
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
      },
      defaultLines: [],
      recipientOptions: [],
      issuerOptions: [],
    };
  },

  computed: {
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  async beforeRouteEnter(to, from, next) {
    try {
      await store.dispatch({
        type: 'ui/invoices/read',
        invoiceUuid: to.params.invoiceUuid,
      });

      const response = await invoicesClient.edit(to.params.invoiceUuid);

      if (!response) next(false);
      if (response.status !== 200) next(false);
      if (response?.headers['content-type'] !== 'application/json') next(false);

      next((vm) => {
        vm.setData(response.data);
      });
    } catch (error) {
      next(false);
    }
  },

  methods: {
    ...mapActions('ui/invoices', {
      updateInvoice: 'update',
    }),

    setData({ data, issuers }) {
      const { accountRelation, invoiceLines, ...restData } = data;

      const { related_account: relatedAccount } = accountRelation;

      this.defaultData = { ...this.defaultData, ...omit(restData, ['can', 'account']) };
      this.defaultLines = [...invoiceLines];

      this.recipientOptions.push({
        label: relatedAccount.name,
        value: relatedAccount.id,
        registrations: relatedAccount.registrations || [],
      });

      this.issuerOptions = issuers.map((item) => ({
        label: item.name,
        value: item.id,
        registrations: item.registrations || [],
      }));
    },

    async doUpdate(formData) {
      if (this.isLoading) return;

      const { invoiceUuid } = this;

      this.errorMessage = '';
      this.status = STATUS_PENDING;

      try {
        await this.updateInvoice({ invoiceUuid, formData });

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;

        if (!error.response) {
          this.errorMessage = 'Something went wrong. Try again later';
        } else if (error.response.status === HTTP_UNPROCESSABLE_ENTITY) {
          this.serverErrors = error.response.data.errors;
          this.errorMessage = error.response.data.message;
        }
      }
    },
  },
};
</script>
